import {
  parseClinicalDocumentContent,
  type ClinicalDocumentContent,
  type ClinicalParagraph,
  type ClinicalTextColor,
  type ClinicalTextRun,
  type ClinicalTextSize,
} from "../shared/clinical";

type RunMarks = Omit<ClinicalTextRun, "text">;

export type ClinicalInlineFormat =
  | { type: "bold" }
  | { type: "italic" }
  | { type: "keyword" }
  | { type: "color"; value: ClinicalTextColor }
  | { type: "size"; value: ClinicalTextSize };

export const defaultClinicalKeywords = [
  "회전근개 부분파열",
  "극상건 부분파열",
  "견봉하 점액낭염",
  "견봉하 충돌 증후군",
  "초음파 유도하",
  "관절운동범위 제한",
  "신경학적 결손",
  "골절",
  "탈구",
  "야간통",
  "주사",
  "시술",
  "초음파",
  "MRI",
  "CT",
  "X-ray",
] as const;

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[character] ?? character,
  );

function markAttributes(run: ClinicalTextRun): string {
  const attributes: string[] = [];
  if (run.bold) attributes.push('data-bold="true"');
  if (run.italic) attributes.push('data-italic="true"');
  if (run.keyword) attributes.push('data-keyword="true"');
  if (run.color) attributes.push(`data-color="${run.color}"`);
  if (run.size) attributes.push(`data-size="${run.size}"`);
  return attributes.length > 0 ? ` ${attributes.join(" ")}` : "";
}

function runClasses(run: ClinicalTextRun): string {
  return [
    "clinical-run",
    run.bold ? "is-bold" : "",
    run.italic ? "is-italic" : "",
    run.keyword ? "is-keyword" : "",
    run.color ? `color-${run.color}` : "",
    run.size ? `size-${run.size}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function renderClinicalContent(content: ClinicalDocumentContent): string {
  return content.blocks
    .map(
      (block) =>
        `<p>${block.runs
          .map(
            (run) =>
              `<span class="${runClasses(run)}"${markAttributes(run)}>${escapeHtml(run.text)}</span>`,
          )
          .join("")}</p>`,
    )
    .join("");
}

function sameMarks(left: ClinicalTextRun, right: ClinicalTextRun): boolean {
  return (
    left.bold === right.bold &&
    left.italic === right.italic &&
    left.keyword === right.keyword &&
    left.color === right.color &&
    left.size === right.size
  );
}

function appendRun(runs: ClinicalTextRun[], text: string, marks: RunMarks): void {
  if (!text) return;
  const run: ClinicalTextRun = { text, ...marks };
  const previous = runs.at(-1);
  if (previous && sameMarks(previous, run)) previous.text += text;
  else runs.push(run);
}

function marksFromElement(element: HTMLElement, inherited: RunMarks): RunMarks {
  const marks: RunMarks = { ...inherited };
  if (
    element.tagName === "STRONG" ||
    element.tagName === "B" ||
    element.dataset.bold === "true"
  ) {
    marks.bold = true;
  }
  if (
    element.tagName === "EM" ||
    element.tagName === "I" ||
    element.dataset.italic === "true"
  ) {
    marks.italic = true;
  }
  if (element.dataset.keyword === "true") {
    marks.keyword = true;
    marks.bold = true;
  }
  const color = element.dataset.color;
  if (
    color === "red" ||
    color === "blue" ||
    color === "green" ||
    color === "amber" ||
    color === "violet"
  ) {
    marks.color = color;
  }
  const size = element.dataset.size;
  if (size === "small" || size === "normal" || size === "large" || size === "xlarge") {
    marks.size = size;
  }
  return marks;
}

function collectInline(node: Node, inherited: RunMarks, runs: ClinicalTextRun[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    appendRun(runs, node.textContent ?? "", inherited);
    return;
  }
  if (!(node instanceof HTMLElement)) return;
  if (node.tagName === "BR") {
    appendRun(runs, "\n", inherited);
    return;
  }
  const marks = marksFromElement(node, inherited);
  for (const child of node.childNodes) collectInline(child, marks, runs);
}

function paragraphFromNode(node: Node): ClinicalParagraph {
  const runs: ClinicalTextRun[] = [];
  collectInline(node, {}, runs);
  return { type: "paragraph", runs: runs.length > 0 ? runs : [{ text: "" }] };
}

export function serializeClinicalEditor(editor: HTMLElement): ClinicalDocumentContent {
  const blocks: ClinicalParagraph[] = [];
  const looseNodes: Node[] = [];
  const flushLooseNodes = (): void => {
    if (looseNodes.length === 0) return;
    const container = document.createElement("span");
    for (const node of looseNodes.splice(0)) container.append(node.cloneNode(true));
    blocks.push(paragraphFromNode(container));
  };

  for (const node of editor.childNodes) {
    if (
      node instanceof HTMLElement &&
      (node.tagName === "P" || node.tagName === "DIV" || node.tagName === "LI")
    ) {
      flushLooseNodes();
      blocks.push(paragraphFromNode(node));
    } else if (node instanceof HTMLElement && node.tagName === "BR") {
      flushLooseNodes();
      blocks.push({ type: "paragraph", runs: [{ text: "" }] });
    } else {
      looseNodes.push(node);
    }
  }
  flushLooseNodes();
  if (blocks.length === 0) blocks.push({ type: "paragraph", runs: [{ text: "" }] });
  return parseClinicalDocumentContent({ schemaVersion: 1, blocks });
}

function styleWrapper(wrapper: HTMLSpanElement, format: ClinicalInlineFormat): void {
  if (format.type === "bold") wrapper.dataset.bold = "true";
  if (format.type === "italic") wrapper.dataset.italic = "true";
  if (format.type === "keyword") {
    wrapper.dataset.keyword = "true";
    wrapper.dataset.bold = "true";
  }
  if (format.type === "color") wrapper.dataset.color = format.value;
  if (format.type === "size") wrapper.dataset.size = format.value;
  wrapper.className = "clinical-run";
}

export function applyInlineFormat(
  editor: HTMLElement,
  format: ClinicalInlineFormat,
): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return false;

  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  const selectedNodes: Text[] = [];
  let candidate = walker.nextNode();
  while (candidate) {
    if (candidate instanceof Text && candidate.data.length > 0 && range.intersectsNode(candidate)) {
      selectedNodes.push(candidate);
    }
    candidate = walker.nextNode();
  }
  if (selectedNodes.length === 0) return false;

  const wrappers: HTMLSpanElement[] = [];
  for (const textNode of selectedNodes.reverse()) {
    const start = textNode === range.startContainer ? range.startOffset : 0;
    const end = textNode === range.endContainer ? range.endOffset : textNode.data.length;
    if (end <= start) continue;
    const selectedText = textNode.splitText(start);
    selectedText.splitText(end - start);
    const wrapper = document.createElement("span");
    styleWrapper(wrapper, format);
    selectedText.parentNode?.insertBefore(wrapper, selectedText);
    wrapper.append(selectedText);
    wrappers.push(wrapper);
  }
  editor.focus();
  return wrappers.length > 0;
}

function escapeRegularExpression(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function emphasizeClinicalKeywords(
  content: ClinicalDocumentContent,
  keywords: readonly string[] = defaultClinicalKeywords,
): ClinicalDocumentContent {
  const usableKeywords = [...keywords]
    .filter((keyword) => keyword.trim().length > 0)
    .sort((left, right) => right.length - left.length);
  if (usableKeywords.length === 0) return content;
  const pattern = new RegExp(`(${usableKeywords.map(escapeRegularExpression).join("|")})`, "giu");

  const blocks = content.blocks.map((block): ClinicalParagraph => {
    const runs: ClinicalTextRun[] = [];
    for (const run of block.runs) {
      if (run.keyword) {
        appendRun(runs, run.text, run);
        continue;
      }
      for (const piece of run.text.split(pattern)) {
        if (!piece) continue;
        const isKeyword = usableKeywords.some(
          (keyword) => keyword.toLocaleLowerCase("ko-KR") === piece.toLocaleLowerCase("ko-KR"),
        );
        appendRun(runs, piece, {
          ...run,
          ...(isKeyword ? { bold: true, keyword: true } : {}),
        });
      }
    }
    return { type: "paragraph", runs: runs.length > 0 ? runs : [{ text: "" }] };
  });
  return parseClinicalDocumentContent({ schemaVersion: 1, blocks });
}

export function applyAutomaticKeywordEmphasis(editor: HTMLElement): ClinicalDocumentContent {
  const content = emphasizeClinicalKeywords(serializeClinicalEditor(editor));
  editor.innerHTML = renderClinicalContent(content);
  return content;
}

export function insertPlainTextAtSelection(editor: HTMLElement, text: string): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return false;
  range.deleteContents();
  const node = document.createTextNode(text.replace(/\r\n?/g, "\n"));
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}
